(() => {
  const quantitySelector = '[data-quantity-selector]';
  const quantityInputSelector = '[data-quantity-input]';
  const decreaseButtonSelector = '[data-decrease-quantity]';

  const updateDecreaseButton = (wrapper) => {
    const input = wrapper.querySelector(quantityInputSelector);
    const decreaseButton = wrapper.querySelector(decreaseButtonSelector);

    if (!input || !decreaseButton) {
      return;
    }

    const parsedMin = Number.parseInt(input.getAttribute('min'), 10);
    const min = Number.isFinite(parsedMin) ? parsedMin : 1;
    const value = Number.parseInt(input.value, 10);
    decreaseButton.disabled = !Number.isFinite(value) || value <= min;
  };

  const updateQuantityState = (container = document) => {
    container.querySelectorAll(quantitySelector).forEach((wrapper) => {
      updateDecreaseButton(wrapper);

      const input = wrapper.querySelector(quantityInputSelector);
      if (!input || input.dataset.quantityStateBound) {
        return;
      }

      input.addEventListener('input', () => updateDecreaseButton(wrapper));
      input.addEventListener('change', () => updateDecreaseButton(wrapper));
      input.dataset.quantityStateBound = 'true';
    });
  };

  const init = () => {
    updateQuantityState();

    document.addEventListener('shopify:section:load', (event) => {
      updateQuantityState(event.target);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

if (!window.customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.intervalId = null;
    }

    connectedCallback() {
      this.cacheElements();
      this.renderLabels();
      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    cacheElements() {
      this.showSeconds = this.dataset.showSeconds === 'true';
      this.valueElements = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.labelElements = this.querySelectorAll('.countdown-timer__label');
      this.endedMessageElement = this.querySelector('.countdown-timer__ended-message');
      this.gridElement = this.querySelector('.countdown-timer__grid');
    }

    renderLabels() {
      if (!this.labelElements.length) {
        return;
      }

      const labelDays = this.dataset.labelDays || 'Days';
      const labelHours = this.dataset.labelHours || 'Hours';
      const labelMinutes = this.dataset.labelMinutes || 'Minutes';
      const labelSeconds = this.dataset.labelSeconds || 'Seconds';
      const labels = this.showSeconds ? [labelDays, labelHours, labelMinutes, labelSeconds] : [labelDays, labelHours, labelMinutes];

      this.labelElements.forEach((element, index) => {
        element.textContent = labels[index];
      });
    }

    tick() {
      const now = new Date();
      const startAt = this.parseDate(this.dataset.startAt);
      const endAt = this.parseDate(this.dataset.endAt);

      if (!endAt) {
        return;
      }

      if (startAt && now < startAt) {
        this.renderTime(this.getTimeParts(startAt.getTime() - now.getTime()));
        this.updateAriaLabel(startAt);
        this.hideEndedMessage();
        return;
      }

      const diff = endAt.getTime() - now.getTime();

      if (diff <= 0) {
        this.handleEndedState();
        return;
      }

      this.showGrid();
      this.hideEndedMessage();
      this.renderTime(this.getTimeParts(diff));
      this.updateAriaLabel(endAt);
    }

    parseDate(value) {
      if (!value) {
        return null;
      }

      const parsedValue = new Date(value);
      if (Number.isNaN(parsedValue.getTime())) {
        return null;
      }

      return parsedValue;
    }

    getTimeParts(milliseconds) {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);

      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      const seconds = totalSeconds % 60;

      return {
        days: this.padTime(days),
        hours: this.padTime(hours),
        minutes: this.padTime(minutes),
        seconds: this.padTime(seconds),
      };
    }

    padTime(value) {
      return String(value).padStart(2, '0');
    }

    renderTime(timeParts) {
      if (this.valueElements.days) {
        this.valueElements.days.textContent = timeParts.days;
      }
      if (this.valueElements.hours) {
        this.valueElements.hours.textContent = timeParts.hours;
      }
      if (this.valueElements.minutes) {
        this.valueElements.minutes.textContent = timeParts.minutes;
      }
      if (this.valueElements.seconds) {
        this.valueElements.seconds.textContent = timeParts.seconds;
      }
    }

    handleEndedState() {
      const state = this.dataset.afterEndState || 'keep_zero';

      if (state === 'hide') {
        this.hideGrid();
        this.hideEndedMessage();
      } else if (state === 'keep_zero') {
        this.showGrid();
        this.hideEndedMessage();
        this.renderTime({ days: '00', hours: '00', minutes: '00', seconds: '00' });
      } else {
        this.hideGrid();
        this.showEndedMessage();
      }

      this.setAttribute('aria-label', 'Countdown ended');
      this.stopTimer();
    }

    showEndedMessage() {
      if (!this.endedMessageElement) {
        return;
      }

      this.endedMessageElement.hidden = false;
      this.endedMessageElement.textContent = this.dataset.afterEndMessage || 'Offer has ended';
    }

    hideEndedMessage() {
      if (!this.endedMessageElement) {
        return;
      }

      this.endedMessageElement.hidden = true;
      this.endedMessageElement.textContent = '';
    }

    hideGrid() {
      if (this.gridElement) {
        this.gridElement.hidden = true;
      }
      this.classList.add('countdown-timer--hidden');
    }

    showGrid() {
      if (this.gridElement) {
        this.gridElement.hidden = false;
      }
      this.classList.remove('countdown-timer--hidden');
    }

    updateAriaLabel(endAt) {
      const days = this.valueElements.days ? this.valueElements.days.textContent : '00';
      const hours = this.valueElements.hours ? this.valueElements.hours.textContent : '00';
      const minutes = this.valueElements.minutes ? this.valueElements.minutes.textContent : '00';
      const seconds = this.valueElements.seconds ? this.valueElements.seconds.textContent : '00';
      const timeLabel = this.showSeconds
        ? `${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`
        : `${days} days, ${hours} hours, ${minutes} minutes`;

      this.setAttribute('aria-label', `Countdown to ${endAt.toISOString()}: ${timeLabel}`);
    }

    stopTimer() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }

  window.customElements.define('countdown-timer', CountdownTimer);
}

/**
 * <x-accordion> — generic accessible accordion custom element.
 *
 * HTML structure (data attributes only, class-agnostic):
 *
 *   <x-accordion>
 *     <div data-accordion-item>
 *       <button data-accordion-trigger>Question</button>
 *       <div data-accordion-body>
 *         <div data-accordion-inner>Answer text</div>
 *       </div>
 *     </div>
 *   </x-accordion>
 *
 * Accessibility:
 *   - trigger gets aria-expanded + aria-controls
 *   - body gets role="region" + aria-labelledby
 *   - Keyboard: Space/Enter on button (native), Escape closes open item
 *   - Hidden items are NOT removed from DOM — CSS controls visibility
 *     via grid-template-rows trick (for smooth height animation).
 */

if (!customElements.get('x-accordion')) {
  let _uid = 0;
  function uid() {
    return `accordion-${Date.now()}-${_uid++}`;
  }

  class Accordion extends HTMLElement {
    connectedCallback() {
      this._init();
    }

    _init() {
      this.querySelectorAll('[data-accordion-item]').forEach((item) => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const body = item.querySelector('[data-accordion-body]');
        if (!trigger || !body) return;

        // Ensure unique IDs for aria linking
        if (!body.id) body.id = uid();
        if (!trigger.id) trigger.id = uid();

        // Initial a11y state
        const isOpen = item.hasAttribute('data-accordion-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        trigger.setAttribute('aria-controls', body.id);
        body.setAttribute('role', 'region');
        body.setAttribute('aria-labelledby', trigger.id);

        trigger.addEventListener('click', () => this._toggle(item, trigger));
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this._close(item, trigger);
        });
      });

      // Allow Escape from anywhere inside the accordion to close all
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeAll();
      });
    }

    // ── Public API ───────────────────────────────────────────────────────────

    open(item) {
      const trigger = item.querySelector('[data-accordion-trigger]');
      item.setAttribute('data-accordion-open', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    close(item) {
      const trigger = item.querySelector('[data-accordion-trigger]');
      this._close(item, trigger);
    }

    // ── Private ──────────────────────────────────────────────────────────────

    _toggle(item, trigger) {
      if (item.hasAttribute('data-accordion-open')) {
        this._close(item, trigger);
      } else {
        if (!this.hasAttribute('data-multi-open')) {
          this._closeAll();
        }
        this.open(item);
      }
    }

    _close(item, trigger) {
      item.removeAttribute('data-accordion-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    _closeAll() {
      this.querySelectorAll('[data-accordion-item][data-accordion-open]').forEach((item) => {
        this.close(item);
      });
    }
  }

  customElements.define('x-accordion', Accordion);
}

if (!customElements.get('marquee-controller')) {
  class MarqueeController extends HTMLElement {
    constructor() {
      super();
      this.position = 0;
      this.speed = 50;
      this.direction = 'ltr';
      this._running = false;
      this.innerElement = null;
      this.boxWidth = 0;
      this.innerElementWidth = 0;
      this.intersectionObserver = null;
      this.resizeTimeout = null;
      this.imageLoadTimeout = null;
      this.viewportWidth = 0;
      this.scheduleRecalc = this.scheduleRecalc.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      if (this.imageLoadTimeout) {
        clearTimeout(this.imageLoadTimeout);
      }
      window.removeEventListener('resize', this.handleResize);
      this.stop();
    }

    init() {
      this.initMarquee();
      this.viewportWidth = window.innerWidth;
      this.initHoverEvents();
      this.initObserver();

      this.handleResize = () => {
        if (window.innerWidth === this.viewportWidth) {
          return;
        }

        this.viewportWidth = window.innerWidth;
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.onResize(), 150);
      };
      window.addEventListener('resize', this.handleResize);
    }

    initMarquee() {
      const innerElement = this.children && this.children[0];

      if (!innerElement) {
        console.error('No child node found in marquee-controller');
        return;
      }

      innerElement.style.position = 'relative';
      this.innerElement = innerElement.cloneNode(true);
      this.speed = window.innerWidth > 768 ? parseInt(this.dataset.scrollingSpeed || 50) : parseInt(this.dataset.scrollingSpeed || 50) / 1.5;
      this.direction = this.dataset.scrollingDirection || 'ltr';

      this.refreshWidths();
      this.setUpChildren();
      this.watchImages();
    }

    refreshWidths() {
      this.boxWidth = this.clientWidth;

      if (this.children[0]) {
        this.innerElementWidth = this.children[0].scrollWidth;
      }
    }

    calculateNumElements() {
      if (!this.innerElementWidth || this.innerElementWidth <= 0) {
        return 2;
      }

      return Math.max(2, Math.ceil(this.boxWidth / this.innerElementWidth) + 1);
    }

    setUpChildren() {
      const qty = this.calculateNumElements();

      if (qty > this.children.length) {
        for (let i = this.children.length; i < qty; i++) {
          this.appendChild(this.innerElement.cloneNode(true));
        }
      } else if (qty < this.children.length) {
        for (let i = qty; i < this.children.length; i++) {
          this.removeChild(this.lastChild);
        }
      }
    }

    nextFrame(delta) {
      this.refreshWidths();
      this.setUpChildren();

      Array.from(this.children).forEach((el) => {
        if (this.direction === 'rtl') {
          el.style.transform = `translateX(${this.position}px)`;
        } else {
          el.style.transform = `translateX(${-this.position}px)`;
        }
      });

      this.position += (this.speed * delta) / 1000;

      if (this.position >= this.innerElementWidth) {
        this.position = this.position % this.innerElementWidth;
      }
    }

    start() {
      this._running = true;
      let lastTime = null;

      const loop = () => {
        if (!this._running) return;

        const now = Date.now();
        const delta = lastTime === null ? 0 : now - lastTime;
        this.nextFrame(delta);
        lastTime = now;

        window.requestAnimationFrame(loop);
      };

      window.requestAnimationFrame(loop);
    }

    stop() {
      this._running = false;
    }

    initHoverEvents() {
      if (this.dataset.pauseOnHover !== 'true') return;

      let windowInFocus = true;

      window.addEventListener('blur', () => {
        windowInFocus = false;
      });

      window.addEventListener('focus', () => {
        windowInFocus = true;
      });

      this.addEventListener('mouseover', () => {
        if (windowInFocus) {
          this.stop();
        }
      });

      this.addEventListener('mouseout', () => {
        if (windowInFocus) {
          this.start();
        }
      });
    }

    initObserver() {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.start();
        } else {
          this.stop();
        }
      });
      this.intersectionObserver.observe(this);
    }

    watchImages() {
      const track = this.children[0];
      if (!track) {
        return;
      }

      track.querySelectorAll('img').forEach((img) => {
        if (img.complete) {
          if (typeof img.decode === 'function') {
            img
              .decode()
              .then(() => this.scheduleRecalc())
              .catch(() => this.scheduleRecalc());
          } else {
            this.scheduleRecalc();
          }
          return;
        }

        img.addEventListener('load', this.scheduleRecalc, { once: true });
      });
    }

    scheduleRecalc() {
      clearTimeout(this.imageLoadTimeout);
      this.imageLoadTimeout = setTimeout(() => {
        this.refreshWidths();
        this.setUpChildren();
      }, 150);
    }

    onResize() {
      const currentPosition = this.position;

      this.stop();
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      this.initMarquee();

      if (this.innerElementWidth > 0) {
        this.position = currentPosition % this.innerElementWidth;
      }

      this.initObserver();
    }
  }

  customElements.define('marquee-controller', MarqueeController);
}

if (!customElements.get('drivers-campaign-popup')) {
  const DIALOG_TRANSITION_MS = 250;

  class DriversCampaignPopup extends HTMLElement {
    constructor() {
      super();
      this.lastTrigger = null;
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogCancel = this.onDialogCancel.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.dialogs = [];
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.dialogs = Array.from(this.querySelectorAll('.drivers-campaign__dialog'));

      this.dialogs.forEach((dialog) => {
        dialog.addEventListener('click', this.onDialogClick);
        dialog.addEventListener('cancel', this.onDialogCancel);
        dialog.addEventListener('close', this.onDialogClose);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);

      this.dialogs.forEach((dialog) => {
        dialog.removeEventListener('click', this.onDialogClick);
        dialog.removeEventListener('cancel', this.onDialogCancel);
        dialog.removeEventListener('close', this.onDialogClose);
      });

      this.dialogs = [];
      this.lastTrigger = null;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    openDialog(dialog) {
      const scroller = dialog.querySelector('.drivers-campaign__dialog-scroller');
      const panel = dialog.querySelector('.drivers-campaign__dialog-panel');

      dialog.classList.remove('is-closing', 'is-visible');
      dialog.showModal();

      if (scroller) {
        scroller.scrollTop = 0;
      }

      document.dispatchEvent(
        new CustomEvent('theme:scroll:lock', {
          bubbles: true,
          detail: scroller || panel || dialog,
        }),
      );

      if (this.prefersReducedMotion()) {
        dialog.classList.add('is-visible');
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dialog.classList.add('is-visible');
        });
      });
    }

    closeDialog(dialog) {
      if (!dialog.open || dialog.classList.contains('is-closing')) {
        return;
      }

      if (this.prefersReducedMotion()) {
        dialog.close();
        return;
      }

      const animatedElement = dialog.querySelector('.drivers-campaign__dialog-panel') || dialog;

      dialog.classList.remove('is-visible');
      dialog.classList.add('is-closing');

      const finishClose = () => {
        dialog.classList.remove('is-closing');

        if (dialog.open) {
          dialog.close();
        }
      };

      const onTransitionEnd = (event) => {
        if (event.target !== animatedElement) {
          return;
        }

        animatedElement.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      };

      animatedElement.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(() => {
        if (!dialog.classList.contains('is-closing')) {
          return;
        }

        animatedElement.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      }, DIALOG_TRANSITION_MS + 50);
    }

    onClick(event) {
      const trigger = event.target.closest('[data-driver-open]');
      if (!trigger) {
        return;
      }

      const dialogId = trigger.getAttribute('aria-controls');
      const dialog = dialogId ? this.querySelector(`#${CSS.escape(dialogId)}`) : null;

      if (!dialog || typeof dialog.showModal !== 'function') {
        return;
      }

      this.lastTrigger = trigger;
      this.openDialog(dialog);
    }

    onKeyDown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const trigger = event.target.closest('[data-driver-open]');
      if (!trigger || event.target !== trigger) {
        return;
      }

      event.preventDefault();
      trigger.click();
    }

    onDialogClick(event) {
      const dialog = event.currentTarget;

      if (event.target.closest('[data-driver-close]')) {
        this.closeDialog(dialog);
        return;
      }

      if (!event.target.closest('.drivers-campaign__dialog-panel')) {
        this.closeDialog(dialog);
      }
    }

    onDialogCancel(event) {
      event.preventDefault();
      this.closeDialog(event.currentTarget);
    }

    onDialogClose(event) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

      const dialog = event.currentTarget;
      if (dialog instanceof HTMLDialogElement) {
        dialog.classList.remove('is-visible', 'is-closing');
      }

      if (!this.lastTrigger) {
        return;
      }

      this.lastTrigger.focus();
      this.lastTrigger = null;
    }
  }

  customElements.define('drivers-campaign-popup', DriversCampaignPopup);
}

if (!customElements.get('fit-info-drawer')) {
  const FIT_INFO_DRAWER_TRANSITION_MS = 300;

  class FitInfoDrawer extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.dialog = null;
      this.panel = null;
      this.lastTrigger = null;
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogCancel = this.handleDialogCancel.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-fit-info-trigger]');
      this.dialog = this.querySelector('[data-fit-info-dialog]');
      this.panel = this.querySelector('[data-fit-info-panel]');

      if (!this.trigger || !this.dialog || !this.panel) {
        return;
      }

      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.dialog.addEventListener('cancel', this.handleDialogCancel);
      this.dialog.addEventListener('close', this.handleDialogClose);
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.handleTriggerClick);
      }
      if (this.dialog) {
        this.dialog.removeEventListener('click', this.handleDialogClick);
        this.dialog.removeEventListener('cancel', this.handleDialogCancel);
        this.dialog.removeEventListener('close', this.handleDialogClose);

        if (this.dialog.open) {
          this.dialog.close();
          this.unlockScroll();
        }
      }

      this.trigger = null;
      this.dialog = null;
      this.panel = null;
      this.lastTrigger = null;
    }

    handleTriggerClick(event) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        return;
      }

      event.preventDefault();
      this.openDrawer();
    }

    openDrawer() {
      if (this.dialog.open) {
        return;
      }

      this.lastTrigger = this.trigger;
      this.dialog.classList.remove('is-closing', 'is-visible');
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.lockScroll();

      requestAnimationFrame(() => {
        if (this.dialog && this.dialog.open) {
          this.dialog.classList.add('is-visible');
        }
      });
    }

    closeDrawer() {
      if (!this.dialog || !this.dialog.open || this.dialog.classList.contains('is-closing')) {
        return;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return;
      }

      this.dialog.classList.remove('is-visible');
      this.dialog.classList.add('is-closing');

      const finishClose = () => {
        this.panel.removeEventListener('transitionend', handleTransitionEnd);
        this.dialog.classList.remove('is-closing');
        if (this.dialog.open) {
          this.dialog.close();
        }
      };

      const handleTransitionEnd = (event) => {
        if (event.target === this.panel && event.propertyName === 'transform') {
          finishClose();
        }
      };

      this.panel.addEventListener('transitionend', handleTransitionEnd);
      window.setTimeout(finishClose, FIT_INFO_DRAWER_TRANSITION_MS + 50);
    }

    handleDialogClick(event) {
      if (event.target === this.dialog || event.target.closest('[data-fit-info-close]')) {
        this.closeDrawer();
      }
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    handleDialogClose() {
      this.dialog.classList.remove('is-closing', 'is-visible');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.unlockScroll();

      if (this.lastTrigger && this.lastTrigger.isConnected) {
        this.lastTrigger.focus();
      }
      this.lastTrigger = null;
    }

    lockScroll() {
      document.documentElement.classList.add('fit-info-drawer-open');
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    }

    unlockScroll() {
      document.documentElement.classList.remove('fit-info-drawer-open');
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }

  customElements.define('fit-info-drawer', FitInfoDrawer);
}

class ProductCard extends HTMLElement {
  static sliderSyncFrames = new WeakMap();
  static sliderObservers = new WeakMap();
  static sliderContainers = new Set();
  static sliderResizeBound = false;

  connectedCallback() {
    if (this.dataset.productCardReady === 'true') return;

    this.dataset.productCardReady = 'true';
    this.addButton = this.querySelector('[data-product-card-add]');
    this.status = this.querySelector('[data-product-card-status]');
    this.addButton?.addEventListener('click', this.handleAddToCart.bind(this));
    this.scheduleSliderHeightSync();
  }

  scheduleSliderHeightSync() {
    const slider = this.closest('[data-slideshow]');
    if (!slider) return;

    ProductCard.sliderContainers.add(slider);
    ProductCard.bindSliderResize();
    ProductCard.observeSlider(slider);
    ProductCard.queueSliderHeightSync(slider);
  }

  static observeSlider(slider) {
    if (ProductCard.sliderObservers.has(slider)) return;

    const observer = new MutationObserver((mutations) => {
      const shouldSync = mutations.some((mutation) => {
        if (mutation.type === 'childList') {
          return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
        }

        return mutation.target === slider;
      });

      if (shouldSync) {
        ProductCard.queueSliderHeightSync(slider);
      }
    });

    observer.observe(slider, { attributes: true, attributeFilter: ['class'], childList: true });
    ProductCard.sliderObservers.set(slider, observer);
  }

  static bindSliderResize() {
    if (ProductCard.sliderResizeBound) return;

    ProductCard.sliderResizeBound = true;
    window.addEventListener('resize', () => {
      ProductCard.sliderContainers.forEach((slider) => {
        if (!slider.isConnected) {
          ProductCard.sliderContainers.delete(slider);
          return;
        }

        ProductCard.queueSliderHeightSync(slider);
      });
    });
  }

  static queueSliderHeightSync(slider, attempt = 0) {
    const pendingFrame = ProductCard.sliderSyncFrames.get(slider);
    if (pendingFrame) {
      window.cancelAnimationFrame(pendingFrame);
    }

    const frame = window.requestAnimationFrame(() => {
      if (!slider.isConnected) return;

      if (!ProductCard.equalizeSliderCards(slider) && attempt < 12) {
        ProductCard.queueSliderHeightSync(slider, attempt + 1);
        return;
      }
    });

    ProductCard.sliderSyncFrames.set(slider, frame);
  }

  static equalizeSliderCards(slider) {
    const cards = Array.from(slider.querySelectorAll('product-card'));
    if (!cards.length) return false;

    cards.forEach((card) => {
      card.classList.remove('product-card--slider-equal-height');
      card.style.removeProperty('--product-card-slider-height');
    });

    const cardHeight = Math.ceil(Math.max(...cards.map((card) => card.getBoundingClientRect().height)));
    if (!cardHeight) return false;

    cards.forEach((card) => {
      card.style.setProperty('--product-card-slider-height', `${cardHeight}px`);
      card.classList.add('product-card--slider-equal-height');
    });

    return true;
  }

  async handleAddToCart() {
    if (!this.addButton || this.addButton.disabled) return;

    const variantId = Number(this.addButton.dataset.variantId);
    if (!variantId) return;

    this.setButtonState('loading');
    this.announce('');

    try {
      const addResponse = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const addedProduct = await addResponse.json();
      if (!addResponse.ok) {
        throw new Error(addedProduct.description || addedProduct.message || this.dataset.errorMessage);
      }

      await this.refreshCart(addedProduct);
      this.setButtonState('success');
      this.announce(this.dataset.addedMessage);

      window.setTimeout(() => this.setButtonState('ready'), 1600);
    } catch (error) {
      this.setButtonState('ready');
      this.announce(error.message || this.dataset.errorMessage);
    }
  }

  async refreshCart(addedProduct) {
    const cartUrl = `${window.theme?.routes?.cart || '/cart'}.js`;
    let cart;

    try {
      const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });

      if (cartResponse.ok) {
        cart = await cartResponse.json();
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
      }
    } catch {
      cart = null;
    }

    const customDrawer = document.querySelector('[data-custom-cart-drawer]');
    if (!customDrawer) {
      const ocuCart = window.Zipify?.Cart?.instance;
      if (ocuCart?.openCart) {
        try {
          if (cart && ocuCart.updateCart) {
            await ocuCart.updateCart(cart);
          }

          await ocuCart.openCart();
          return;
        } catch {}
      }
    }

    const drawer = customDrawer || document.querySelector('[data-drawer="drawer-cart"]');
    if (drawer) {
      drawer.dispatchEvent(
        new CustomEvent('theme:drawer:open', {
          detail: { variant: addedProduct, reinit: true },
          bubbles: true,
        }),
      );
    } else {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    }
  }

  setButtonState(state) {
    if (!this.addButton) return;

    this.addButton.classList.toggle('is-loading', state === 'loading');
    this.addButton.classList.toggle('is-success', state === 'success');
    this.addButton.disabled = state === 'loading' || state === 'success';
    this.addButton.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

(() => {
  const floatingCartSelector = '[data-floating-add-to-cart]';
  const primaryAddButtonSelector = '.product__form__actions .product__submit__add';
  const footerGroupSelector = [
    '#shopify-section-group-group-footer',
    '#shopify-section-group-group-footer-giveaway',
    '#shopify-section-group-group-footer-motorsports',
    '#shopify-section-group-footer',
    '#shopify-section-group-footer-giveaway',
    '#shopify-section-group-footer-motorsports',
  ].join(', ');
  const footerFallbackSelector =
    'footer, .subfooter, [data-section-type="footer"], [data-section-type="subfooter"]';
  const controllers = new WeakMap();

  const getFloatingCarts = (container) => {
    if (!container) return [];

    if (typeof container.matches === 'function' && container.matches(floatingCartSelector)) {
      return [container];
    }

    return typeof container.querySelectorAll === 'function'
      ? Array.from(container.querySelectorAll(floatingCartSelector))
      : [];
  };

  const setFloatingCartVisibility = (floatingCart, visible) => {
    floatingCart.classList.toggle('is-visible', visible);
    floatingCart.setAttribute('aria-hidden', String(!visible));
    floatingCart.inert = !visible;
  };

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;
  };

  const getFooterGroupElements = () => {
    const footerGroup = document.querySelector(footerGroupSelector);
    if (footerGroup) {
      return [footerGroup];
    }

    return Array.from(document.querySelectorAll(footerFallbackSelector));
  };

  const getRenderedBounds = (element) => {
    const rect = element.getBoundingClientRect();
    if (rect.bottom > rect.top) {
      return rect;
    }

    const descendants = element.querySelectorAll('[data-section-id], [data-section-type], footer, section');
    let top = Number.POSITIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;

    descendants.forEach((descendant) => {
      const descendantRect = descendant.getBoundingClientRect();
      if (descendantRect.bottom <= descendantRect.top) return;

      top = Math.min(top, descendantRect.top);
      bottom = Math.max(bottom, descendantRect.bottom);
    });

    if (top !== Number.POSITIVE_INFINITY) {
      return { top, bottom };
    }

    return rect;
  };

  const isFooterGroupOverlappingFloatingCart = (footerGroupElements, floatingCart) => {
    let top = Number.POSITIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;

    footerGroupElements.forEach((footerGroupElement) => {
      const bounds = getRenderedBounds(footerGroupElement);
      if (bounds.bottom <= bounds.top) return;

      top = Math.min(top, bounds.top);
      bottom = Math.max(bottom, bounds.bottom);
    });

    if (top === Number.POSITIVE_INFINITY) return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const floatingCartTop = viewportHeight - floatingCart.offsetHeight;

    return top < viewportHeight && bottom > floatingCartTop;
  };

  const initializeFloatingCart = (floatingCart) => {
    if (controllers.has(floatingCart)) return;

    const productSection = floatingCart.closest('[data-section-type="product"]');
    const primaryAddButton = productSection?.querySelector(primaryAddButtonSelector);

    if (!productSection || !primaryAddButton) {
      setFloatingCartVisibility(floatingCart, false);
      controllers.set(floatingCart, { observer: null, cleanup: null });
      return;
    }

    if ('IntersectionObserver' in window) {
      let primaryAddButtonIsVisible = true;
      const updateVisibility = () => {
        const footerGroupElements = getFooterGroupElements();
        const footerGroupOverlapsFloatingCart = isFooterGroupOverlappingFloatingCart(
          footerGroupElements,
          floatingCart,
        );

        setFloatingCartVisibility(
          floatingCart,
          !primaryAddButtonIsVisible && !footerGroupOverlapsFloatingCart,
        );
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === primaryAddButton) {
            primaryAddButtonIsVisible = entry.isIntersecting;
          }
        });

        updateVisibility();
      });

      observer.observe(primaryAddButton);
      window.addEventListener('scroll', updateVisibility, { passive: true });
      window.addEventListener('resize', updateVisibility);
      controllers.set(floatingCart, {
        observer,
        update: updateVisibility,
        cleanup: () => {
          window.removeEventListener('scroll', updateVisibility);
          window.removeEventListener('resize', updateVisibility);
        },
      });
      return;
    }

    const updateVisibility = () => {
      const primaryAddButtonIsVisible = isInViewport(primaryAddButton);
      const footerGroupElements = getFooterGroupElements();
      const footerGroupOverlapsFloatingCart = isFooterGroupOverlappingFloatingCart(
        footerGroupElements,
        floatingCart,
      );

      setFloatingCartVisibility(
        floatingCart,
        !primaryAddButtonIsVisible && !footerGroupOverlapsFloatingCart,
      );
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    controllers.set(floatingCart, {
      observer: null,
      update: updateVisibility,
      cleanup: () => {
        window.removeEventListener('scroll', updateVisibility);
        window.removeEventListener('resize', updateVisibility);
      },
    });
  };

  const initializeFloatingCarts = (container = document) => {
    getFloatingCarts(container).forEach(initializeFloatingCart);
  };

  const refreshFloatingCarts = () => {
    getFloatingCarts(document).forEach((floatingCart) => {
      controllers.get(floatingCart)?.update?.();
    });
  };

  const cleanupFloatingCarts = (container) => {
    getFloatingCarts(container).forEach((floatingCart) => {
      const controller = controllers.get(floatingCart);
      if (!controller) return;

      controller.observer?.disconnect();
      controller.cleanup?.();
      controllers.delete(floatingCart);
    });
  };

  const init = () => {
    initializeFloatingCarts();

    document.addEventListener('shopify:section:load', (event) => {
      initializeFloatingCarts(event.target);
      refreshFloatingCarts();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      cleanupFloatingCarts(event.target);
      refreshFloatingCarts();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

if (!window.customElements.get('scheduled-product-badge')) {
  class ScheduledProductBadge extends HTMLElement {
    connectedCallback() {
      this.updateVisibility();
    }

    disconnectedCallback() {
      window.clearTimeout(this.scheduleTimeout);
    }

    updateVisibility() {
      window.clearTimeout(this.scheduleTimeout);

      const startAt = this.parseDate(this.dataset.startAt);
      const endAt = this.parseDate(this.dataset.endAt);
      const now = Date.now();

      if (Number.isNaN(startAt) || Number.isNaN(endAt)) {
        this.hidden = true;
        this.dataset.scheduleReady = 'true';
        return;
      }

      this.hidden = (startAt !== null && now < startAt) || (endAt !== null && now >= endAt);
      this.dataset.scheduleReady = 'true';

      let nextChangeAt = null;
      if (startAt !== null && now < startAt) {
        nextChangeAt = startAt;
      } else if (endAt !== null && now < endAt) {
        nextChangeAt = endAt;
      }

      if (nextChangeAt !== null) {
        const delay = Math.min(Math.max(nextChangeAt - now + 25, 0), 2147483647);
        this.scheduleTimeout = window.setTimeout(() => this.updateVisibility(), delay);
      }
    }

    parseDate(value) {
      if (!value) {
        return null;
      }

      const timestamp = new Date(String(value).trim()).getTime();
      return Number.isNaN(timestamp) ? NaN : timestamp;
    }
  }

  window.customElements.define('scheduled-product-badge', ScheduledProductBadge);
}

if (!window.customElements.get('product-badges-container')) {
  class ProductBadgesContainer extends HTMLElement {
    connectedCallback() {
      this.priorityObserver = new MutationObserver(() => this.updatePriority());
      this.priorityObserver.observe(this, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['hidden', 'data-schedule-ready'],
      });
      this.updatePriority();
    }

    disconnectedCallback() {
      this.priorityObserver?.disconnect();
    }

    updatePriority() {
      if (this.dataset.globalBadgePriority !== 'true') {
        return;
      }

      const globalBadges = this.querySelectorAll('[data-badge-source="global"]');
      const globalsAreReady = Array.from(globalBadges).every(
        (badge) => badge.dataset.scheduleReady === 'true'
      );

      if (!globalsAreReady) {
        return;
      }

      const globalBadgeIsVisible = Array.from(globalBadges).some((badge) => !badge.hidden);
      this.querySelectorAll('[data-badge-source="product"]').forEach((badge) => {
        const alwaysShowWithGlobalBadges = badge.dataset.showWithGlobalBadges === 'true';
        badge.classList.toggle(
          'product-badge--priority-hidden',
          globalBadgeIsVisible && !alwaysShowWithGlobalBadges
        );
      });
    }
  }

  window.customElements.define('product-badges-container', ProductBadgesContainer);
}
